import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import InsertPersonalFieldsDialog from './InsertPersonalFieldsDialog';
import CashdeskTicketPreview from './CashdeskTicketPreview';
import CashdeskToolbar, { CashdeskPanel } from './creation/cashdesk/CashdeskToolbar';
import CashdeskOfferPanel from './creation/cashdesk/CashdeskOfferPanel';
import CashdeskTextPanel from './creation/cashdesk/CashdeskTextPanel';
import useCashdeskBlocks from './creation/cashdesk/useCashdeskBlocks';
import { CreationPayload, InitialPayload } from './creation/shared/payloadReaders';

interface CashdeskCreationSectionProps {
  offerCount?: number;
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  onOfferIndicesChange?: (indices: Set<number>) => void;
  onOfferRemoved?: (offerIndex: number) => void;
  initialPayload?: InitialPayload;
  onPayloadChange?: (payload: CreationPayload) => void;
}

const hintSx = { fontStyle: 'italic', fontSize: '0.75rem' };

function CashdeskCreationSection({ offerCount = 1, ...options }: CashdeskCreationSectionProps) {
  const ticket = useCashdeskBlocks({ offerCount, ...options });
  const [openPanel, setOpenPanel] = useState<CashdeskPanel>(null);
  const [personalFieldsOpen, setPersonalFieldsOpen] = useState(false);

  const handleInsertBarcode = (anchor: string) => {
    ticket.addBarcodeBlock(anchor);
    setOpenPanel(null);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <CashdeskTicketPreview
        blocks={ticket.blocks}
        selectedId={ticket.selectedId}
        onSelect={ticket.setSelectedId}
        onUpdateBlock={ticket.updateBlock}
        onRemoveBlock={ticket.removeBlock}
      />

      <Box sx={{ flex: 1, minWidth: 280 }}>
        <CashdeskToolbar
          openPanel={openPanel}
          onTogglePanel={panel => setOpenPanel(current => (current === panel ? null : panel))}
          onInsertField={() => setPersonalFieldsOpen(true)}
          onImage={src => ticket.addBlock({ type: 'image', content: '', src, width: 150, height: 100 })}
        />

        {openPanel === 'offer' && <CashdeskOfferPanel offerCount={offerCount} onInsert={handleInsertBarcode} />}

        {openPanel === 'text' && (
          <CashdeskTextPanel
            selected={ticket.selected}
            onAddText={() => ticket.addBlock({ type: 'text', content: 'Zone de texte', width: 200, height: 60 })}
            onUpdate={patch => ticket.selected && ticket.updateBlock(ticket.selected.id, patch)}
          />
        )}

        {ticket.blocks.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={hintSx}>
            Utilisez les outils ci-dessus pour ajouter des éléments dans le ticket.
          </Typography>
        ) : !ticket.selected && (
          <Typography variant="caption" color="text.secondary" sx={hintSx}>
            Cliquez sur un bloc dans le ticket pour le sélectionner et le modifier.
          </Typography>
        )}
      </Box>

      <InsertPersonalFieldsDialog
        open={personalFieldsOpen}
        onClose={() => setPersonalFieldsOpen(false)}
        onInsert={ticket.insertPersonalField}
        channel="cashdesk"
      />
    </Box>
  );
}

export default CashdeskCreationSection;
